import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Popup } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { useBlockedFeatures } from "src/hooks/useBlockedFeatures";
import useModulePermissions from "src/hooks/useModulePermissions";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import { GetEntriesBySource, DeleteEntry } from "../../../../../../graphql/entries";
import TransactionInfoBar from "../transactionInfoBar";
import DocumentInfoBar from "../documentInfoBar";
import CreateJournalEntryModal from "./CreateJournalEntryModal";
import DevengarModal from "./DevengarModal";
import { getDeleteEntryConfirmMessage } from "./entryDevengoHelpers";
import {
  resolveDocumentEntryAmountClp,
  resolveDocumentEmissionSourceAmountClp,
  resolveTransactionEntryAmountClp,
  JOURNAL_ENTRY_CURRENCY,
} from "./cheatSheet";
import EntryRowList from "./EntryRowList";
import "../style.scss";
import "../reconcileComments.css";
import "./reconcileContabilizar.scss";

/**
 * ReconcileContabilizar - Component for accounting journal entries
 * Used when opened from reconcile sidebar (transaction/document)
 */
const ReconcileContabilizar = ({ documentData, onEntryCreated, onRefetchSource }) => {
  const { t } = useTranslation("accounting");
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { editData, options } = openSidebar;
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { isFeatureBlocked } = useBlockedFeatures();
  const isAsientoBlocked = isFeatureBlocked('asiento');
  const { canCreateAsiento, canEditAsiento } = useModulePermissions();
  const puedeCrearAsiento = canCreateAsiento();
  const puedeEditarAsiento = canEditAsiento();
  const isDocument = options?.type == "documents";
  
  // Use documentData prop if provided (most up-to-date), otherwise fall back to editData
  const currentDocumentData = isDocument && documentData ? documentData : editData;

  // Modal state
  const [showJournalEntryModal, setShowJournalEntryModal] = useState(false);

  // Variables for fetching entries
  const sourceId = currentDocumentData?.id || currentDocumentData?._id || editData?.id || editData?._id;
  const sourceType = isDocument ? 'DOCUMENT' : 'TRANSACTION';
  
  // For reconcile route: use entries by source
  const entriesBySourceVariables = useMemo(() => ({
    companyId: selectedCompany?.id,
    sourceId: sourceId,
    sourceType: sourceType,
  }), [selectedCompany?.id, sourceId, sourceType]);

  const { data: entriesBySourceData, fetching: fetchingEntriesBySource, reexecuteQuery: refetchEntriesBySource } =
    GetEntriesBySource({
      ...entriesBySourceVariables,
      pause: !selectedCompany?.id || !sourceId,
    });
  const { reexecuteMutation: deleteEntryMutation } = DeleteEntry();

  const [entryToEdit, setEntryToEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [entryToDuplicate, setEntryToDuplicate] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [entryToReverse, setEntryToReverse] = useState(null);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [entryToDevengar, setEntryToDevengar] = useState(null);
  const [showDevengarModal, setShowDevengarModal] = useState(false);

  // Get entries array for this document/transaction source (includes matchEntry where it is a source)
  const entries = entriesBySourceData?.entriesBySource || [];
  const selectedDoc = currentDocumentData || editData;
  const isAnnulledDocument = selectedDoc?.meta?.isValid === false;
  const isProtestedReceivedDocument =
    selectedDoc?.meta?.isReceived === true &&
    selectedDoc?.meta?.isProtested === true;
  const isPendingAcceptanceDocument = selectedDoc?.meta?.state === "PENDIENTE";
  const isEntryCreationDisabled =
    selectedDoc?.entries?.completed === true ||
    selectedDoc?.entryStatus?.completed === true ||
    isAnnulledDocument ||
    isProtestedReceivedDocument ||
    isPendingAcceptanceDocument;

  // Refresh entries when component mounts or when sourceId changes
  useEffect(() => {
    if (selectedCompany?.id && sourceId && refetchEntriesBySource) {
      refetchEntriesBySource({ requestPolicy: 'network-only' });
    }
  }, [selectedCompany?.id, sourceId]);

  const handleEditEntry = (entry) => {
    setEntryToDuplicate(null);
    setShowDuplicateModal(false);
    setEntryToReverse(null);
    setShowReverseModal(false);
    setEntryToEdit(entry);
    setShowEditModal(true);
  };

  const handleDuplicateEntry = (entry) => {
    setEntryToEdit(null);
    setShowEditModal(false);
    setEntryToReverse(null);
    setShowReverseModal(false);
    setEntryToDuplicate(entry);
    setShowDuplicateModal(true);
  };

  const handleReverseEntry = (entry) => {
    if (entry?.reversed === true) return;
    setEntryToEdit(null);
    setShowEditModal(false);
    setEntryToDuplicate(null);
    setShowDuplicateModal(false);
    setEntryToReverse(entry);
    setShowReverseModal(true);
  };

  const handleDevengarEntry = (entry) => {
    if (entry?.reversed === true) return;
    setEntryToDevengar(entry);
    setShowDevengarModal(true);
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(getDeleteEntryConfirmMessage(entry, t))) return;
    const entryId = entry.id || entry._id;
    if (!selectedCompany?.id || !entryId) return;
    const result = await deleteEntryMutation({
      companyId: selectedCompany.id,
      entriesId: [String(entryId)],
    });
    if (result.error) {
      console.error("Error deleting entry:", result.error);
      const msg =
        result.error?.message ||
        t("entries.deleteError");
      dispatch(
        importAlertState({
          content: msg,
          state: "error",
          duration: 4,
        })
      );
      return;
    }
    if (refetchEntriesBySource) {
      refetchEntriesBySource({ requestPolicy: "network-only" });
    }
    if (onRefetchSource) onRefetchSource();
  };

  // Calculate debit and credit totals from all entries
  const { debitTotal, creditTotal } = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    entries.forEach(entry => {
      entry.entries.forEach(row => {
        totalDebit += parseFloat(row.debit) || 0;
        totalCredit += parseFloat(row.credit) || 0;
      });
    });
    return { debitTotal: totalDebit, creditTotal: totalCredit };
  }, [entries]);

  return (
    <div className="reconcileForm">
      {isDocument ? (
        <DocumentInfoBar editData={currentDocumentData || editData} />
      ) : (
        <TransactionInfoBar editData={editData} />
      )}
      
      {/* Action Buttons and Balance Summary */}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        {!isDocument ? (
          <div className="tw-text-sm tw-text-gray-700 d-flex align-items-center">
            <span className="tw-font-semibold">{t("balance.debitSum")}</span>
            <span style={{ marginLeft: '4px' }}>{formatMoneyWithCurrency(debitTotal, JOURNAL_ENTRY_CURRENCY)}</span>
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>-</span>
            <span className="tw-font-semibold">{t("balance.creditSum")}</span>
            <span style={{ marginLeft: '4px' }}>{formatMoneyWithCurrency(creditTotal, JOURNAL_ENTRY_CURRENCY)}</span>
          </div>
        ) : (
          <div></div>
        )}
        {isDocument && !isAsientoBlocked && puedeCrearAsiento && (() => {
          const blockReason = isAnnulledDocument
            ? t("blocks.annulledDoc")
            : isProtestedReceivedDocument
              ? t("blocks.protestedDoc")
              : isPendingAcceptanceDocument
                ? t("blocks.pendingAcceptanceDoc")
                : null;

          const button = (
            <Button
              type="button"
              color="blue"
              size="mini"
              onClick={() => setShowJournalEntryModal(true)}
              disabled={isEntryCreationDisabled}
            >
              {t("entries.newEntry")}
              <i className="arrow up right icon" style={{ marginLeft: '0.5em' }}></i>
            </Button>
          );

          return (
            <div className="d-flex agendaSubmitButton align-items-center">
              {blockReason ? (
                <Popup content={blockReason} trigger={<span>{button}</span>} />
              ) : (
                button
              )}
            </div>
          );
        })()}
      </div>
      
      {/* Horizontal Divider */}
      <Divider className="mb-4" />

      {/* Display Created Entries */}
      <div className="reconcile-contabilizar-entries-wrapper">
        <EntryRowList
          entries={entries}
          fetching={fetchingEntriesBySource}
          classNamePrefix="entry"
          onEditEntry={puedeEditarAsiento ? handleEditEntry : undefined}
          onDuplicateEntry={puedeCrearAsiento && !isAnnulledDocument ? handleDuplicateEntry : undefined}
          onReverseEntry={puedeCrearAsiento && !isAnnulledDocument ? handleReverseEntry : undefined}
          onDevengarEntry={puedeCrearAsiento && !isAnnulledDocument ? handleDevengarEntry : undefined}
          onDeleteEntry={puedeEditarAsiento ? handleDeleteEntry : undefined}
        />
        </div>

      {/* Journal Entry Modal */}
      <CreateJournalEntryModal
        open={showJournalEntryModal}
        onClose={() => {
          setShowJournalEntryModal(false);
        }}
        onEntryCreated={() => {
          // Refetch entries immediately after successful creation
          if (refetchEntriesBySource) {
            refetchEntriesBySource({ requestPolicy: 'network-only' });
          }
          // Call parent callback to switch to Asientos tab
          if (onEntryCreated) {
            onEntryCreated();
          }
        }}
        editData={{
          originType: isDocument ? ((currentDocumentData || editData)?.documentType || 'TAX_DOCUMENT') : 'TRANSACTION',
          localAmount: (() => {
            const row = currentDocumentData || editData;
            if (!row) return 0;
            return isDocument
              ? resolveDocumentEmissionSourceAmountClp(row)
              : resolveTransactionEntryAmountClp(row);
          })(),
          description: (() => {
            const d = currentDocumentData || editData;
            if (!d) return '';
            if (typeof d.description === 'string' && d.description.trim()) return d.description.trim();
            // Do not pass meta.description for documents: it is often an array of line items (breaks glosa / input).
            if (isDocument) return '';
            const md = d.meta?.description;
            return typeof md === 'string' && md.trim() ? md.trim() : '';
          })(),
          // Explicit sources array with type and id
          sources: (currentDocumentData || editData) ? [{
            type: isDocument ? 'DOCUMENT' : 'TRANSACTION',
            id: (currentDocumentData || editData).id || (currentDocumentData || editData)._id
          }] : [],
          support: {
            transactions: !isDocument && editData ? [editData] : [],
            documents: isDocument && (currentDocumentData || editData) ? [(currentDocumentData || editData)] : [],
          },
          // Keep origin for conditional rendering
          origin: isDocument ? 'DOCUMENT' : 'TRANSACTION',
        }}
      />

      {/* Edit entry modal */}
      <CreateJournalEntryModal
        open={showEditModal && !!entryToEdit}
        onClose={() => {
          setEntryToEdit(null);
          setShowEditModal(false);
        }}
        onEntryCreated={() => {
          if (refetchEntriesBySource) {
            refetchEntriesBySource({ requestPolicy: "network-only" });
          }
          setEntryToEdit(null);
          setShowEditModal(false);
        }}
        existingEntry={entryToEdit}
      />

      {/* Duplicate entry modal */}
      <CreateJournalEntryModal
        open={showDuplicateModal && !!entryToDuplicate}
        onClose={() => {
          setEntryToDuplicate(null);
          setShowDuplicateModal(false);
        }}
        onEntryCreated={() => {
          if (refetchEntriesBySource) {
            refetchEntriesBySource({ requestPolicy: 'network-only' });
          }
          if (onEntryCreated) {
            onEntryCreated();
          }
          setEntryToDuplicate(null);
          setShowDuplicateModal(false);
        }}
        existingEntry={entryToDuplicate}
        duplicateMode
      />

      <CreateJournalEntryModal
        open={showReverseModal && !!entryToReverse}
        onClose={() => {
          setEntryToReverse(null);
          setShowReverseModal(false);
        }}
        onEntryCreated={() => {
          if (refetchEntriesBySource) {
            refetchEntriesBySource({ requestPolicy: 'network-only' });
          }
          if (onEntryCreated) {
            onEntryCreated();
          }
          setEntryToReverse(null);
          setShowReverseModal(false);
        }}
        existingEntry={entryToReverse}
        reverseMode
      />

      <DevengarModal
        open={showDevengarModal && !!entryToDevengar}
        onClose={() => {
          setEntryToDevengar(null);
          setShowDevengarModal(false);
        }}
        sourceEntry={entryToDevengar}
        onDevengado={() => {
          if (refetchEntriesBySource) {
            refetchEntriesBySource({ requestPolicy: 'network-only' });
          }
          if (onEntryCreated) {
            onEntryCreated();
          }
        }}
      />
    </div>
  );
};

export default ReconcileContabilizar;


